/**
 * DecisionFlow — RateTableManager
 * ui/src/components/tenant-admin/RateTableManager.jsx
 *
 * S697 — GAP-PDV-05 (Rate Table Integration)
 * Spec authority: comp_policies_spec_v2_12.md S28.12
 *
 * Inline embedded modifier manager for ARR attainment rate table configuration.
 * Renders in Configure step (Step 3) under "Earnings Structure" section.
 *
 * Plan types in scope:
 *   arr_attainment, revenue_type_differentiated, territory_based,
 *   overlay_specialist, grr_nrr_retention, draw_guarantee
 *
 * Architecture decisions (S28.3):
 *   - No separate approve button — consolidated into plan_component approval (CA-2)
 *   - No navigation — embedded inline, no modal, no route
 *   - No chart required — editable band table is the authoritative surface
 *   - Ceiling fields are static text (no shell-update endpoint in S697 scope)
 *   - Both PATH A (library) and PATH B (custom) receive pre-populated defaults
 *
 * Props:
 *   mode           — 'admin' | 'wizard'
 *   period         — { fiscal_period_id, period_name } | null
 *   embedded       — always true in wizard Configure step
 *   planId         — plan identifier (required)
 *   planComponentId — plan component identifier (required)
 *   fiscalPeriodId  — fiscal period identifier (optional)
 *   planTypeKey    — plan type key for catalog default fallback (optional)
 *   readOnly       — disables all edits when true
 *   onComplete     — callback(bool) fired whenever completion state changes
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from '../../auth/AuthContext.jsx';
import { apiGet, apiPost } from '../../api/client';

const MINIMUM_BAND_COUNT = 2;
const DECIMAL_PRECISION = 4;
const DEFAULT_CEILING_MINIMUM = 0.85;
const DEFAULT_CEILING_MAXIMUM = 1.0;
const PAYOUT_MINIMUM = 0.0;
const PAYOUT_MAXIMUM = 3.0;
const VALID_INTERPOLATION_VALUES = new Set(['linear', 'step']);

function createClientRowKey(prefix = 'row') {
  if (
    typeof crypto !== 'undefined'
    && typeof crypto.randomUUID === 'function'
  ) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 11)}`;
}

function normalizeComparableNumber(value) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Number(parsed.toFixed(DECIMAL_PRECISION));
}

function numbersEqual(left, right) {
  const normalizedLeft = normalizeComparableNumber(left);
  const normalizedRight = normalizeComparableNumber(right);

  if (normalizedLeft === null || normalizedRight === null) {
    return false;
  }

  return normalizedLeft === normalizedRight;
}

function parseDecimalValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return normalizeComparableNumber(parsed);
}

function percentInputToDecimal(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return value;
  }

  return normalizeComparableNumber(parsed / 100);
}

function decimalToPercentInput(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return '';
  }

  return Number((parsed * 100).toFixed(2));
}

function formatPercent(value, maximumFractionDigits = 2) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return '—';
  }

  return `${(parsed * 100).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  })}%`;
}

function normalizeIdentifier(value) {
  if (value === null || value === undefined) {
    return null;
  }

  return String(value);
}

function identifiersEqual(left, right) {
  const normalizedLeft = normalizeIdentifier(left);
  const normalizedRight = normalizeIdentifier(right);

  if (normalizedLeft === null || normalizedRight === null) {
    return false;
  }

  return normalizedLeft === normalizedRight;
}

function sortRowsBySequence(rows) {
  return [...rows].sort((left, right) => {
    const leftSequence = Number(left.band_sequence);
    const rightSequence = Number(right.band_sequence);

    if (!Number.isFinite(leftSequence) && !Number.isFinite(rightSequence)) {
      return 0;
    }

    if (!Number.isFinite(leftSequence)) {
      return 1;
    }

    if (!Number.isFinite(rightSequence)) {
      return -1;
    }

    return leftSequence - rightSequence;
  });
}

function resequenceRows(rows) {
  return rows.map((row, index) => ({
    ...row,
    band_sequence: index + 1,
  }));
}

function normalizeLoadedRow(row, index) {
  return {
    clientRowKey:
      row.clientRowKey
      || (
        row.rate_table_row_id
          ? `persisted-${row.rate_table_row_id}`
          : createClientRowKey('loaded')
      ),
    rate_table_row_id: row.rate_table_row_id ?? null,
    band_sequence: Number(row.band_sequence) || index + 1,
    from_pct: parseDecimalValue(row.from_pct),
    to_pct: parseDecimalValue(row.to_pct),
    payout_pct: parseDecimalValue(row.payout_pct),
    interpolation: String(row.interpolation || '').toLowerCase(),
    is_accelerator:
      typeof row.is_accelerator === 'boolean'
        ? row.is_accelerator
        : Number(row.from_pct) >= 1.0,
  };
}

function normalizeLoadedRows(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }

  return resequenceRows(
    sortRowsBySequence(rows).map(normalizeLoadedRow),
  );
}

function cloneRows(rows) {
  return rows.map((row) => ({ ...row }));
}

function extractRateTableItems(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  // GET /rate-tables returns {"data": [...]} (rate_tables_router.py list_rate_tables)
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.rate_tables)) {
    return payload.rate_tables;
  }

  return [];
}

function extractPersistedRows(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.rows)) {
    return payload.rows;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.rate_table?.rows)) {
    return payload.rate_table.rows;
  }

  return null;
}

function extractCreatedRateTable(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (payload.rate_table && typeof payload.rate_table === 'object') {
    return payload.rate_table;
  }

  if (payload.item && typeof payload.item === 'object') {
    return payload.item;
  }

  if (payload.rate_table_id) {
    return payload;
  }

  return null;
}

function extractDefaultRateTable(payload) {
  const candidates = [
    payload?.default_rate_table,
    payload?.rate_table?.default_rate_table,
    payload?.item?.default_rate_table,
    payload?.defaults?.rate_table,
  ];

  return (
    candidates.find(
      (candidate) =>
        candidate
        && typeof candidate === 'object'
        && Array.isArray(candidate.bands),
    )
    || null
  );
}

function extractPlanTypeItems(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.plan_types)) {
    return payload.plan_types;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
}

function getPlanTypeKey(planType) {
  return (
    planType?.plan_type_key
    ?? planType?.type_key
    ?? planType?.key
    ?? planType?.plan_type
    ?? null
  );
}

function createEmptyValidation() {
  return {
    isValid: false,
    tableErrors: [],
    rowErrors: {},
    warnings: [],
  };
}

function addRowFieldError(rowErrors, rowKey, field, message) {
  if (!rowErrors[rowKey]) {
    rowErrors[rowKey] = {
      from_pct: [],
      to_pct: [],
      payout_pct: [],
      interpolation: [],
      row: [],
    };
  }

  rowErrors[rowKey][field].push(message);
}

function validateRateTableConfiguration(rateTable, rows) {
  const tableErrors = [];
  const rowErrors = {};
  const warnings = [];

  if (!rateTable) {
    tableErrors.push(
      'A rate table must be created for this plan component.',
    );

    return {
      isValid: false,
      tableErrors,
      rowErrors,
      warnings,
    };
  }

  if (!rateTable.plan_component_id) {
    tableErrors.push(
      'The rate table is not linked to a plan component.',
    );
  }

  if (!rateTable.role_id) {
    tableErrors.push(
      'The rate table does not have a role assignment.',
    );
  }

  if (!rateTable.segment_id) {
    tableErrors.push(
      'The rate table does not have a segment assignment.',
    );
  }

  if (!rateTable.effective_date) {
    tableErrors.push(
      'The rate table does not have an effective date.',
    );
  }

  if (
    rateTable.end_date
    && rateTable.effective_date
    && String(rateTable.end_date) < String(rateTable.effective_date)
  ) {
    tableErrors.push(
      'The end date cannot be earlier than the effective date.',
    );
  }

  const attainmentCeiling = parseDecimalValue(
    rateTable.attainment_ceiling_pct,
  );

  const defaultCeiling = parseDecimalValue(
    rateTable.default_ceiling_pct,
  );

  if (attainmentCeiling === null) {
    tableErrors.push(
      'The recognized attainment ceiling is missing.',
    );
  } else if (attainmentCeiling <= 1.0) {
    tableErrors.push(
      'The recognized attainment ceiling must be greater than 100%.',
    );
  }

  if (defaultCeiling === null) {
    tableErrors.push(
      'The payout ceiling is missing.',
    );
  } else {
    if (defaultCeiling < DEFAULT_CEILING_MINIMUM) {
      tableErrors.push(
        'The payout ceiling cannot be lower than 85% of variable OTE.',
      );
    }

    if (defaultCeiling > DEFAULT_CEILING_MAXIMUM) {
      tableErrors.push(
        'The payout ceiling cannot exceed 100% of variable OTE.',
      );
    }
  }

  if (!Array.isArray(rows) || rows.length < MINIMUM_BAND_COUNT) {
    tableErrors.push(
      `At least ${MINIMUM_BAND_COUNT} rate bands are required.`,
    );
  }

  const orderedRows = sortRowsBySequence(
    Array.isArray(rows) ? rows : [],
  );

  const seenSequences = new Set();

  orderedRows.forEach((row, index) => {
    const rowKey = row.clientRowKey || `row-${index}`;
    const expectedSequence = index + 1;
    const sequence = Number(row.band_sequence);

    if (!Number.isInteger(sequence) || sequence <= 0) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'row',
        'Band sequence must be a positive integer.',
      );
    } else {
      if (seenSequences.has(sequence)) {
        addRowFieldError(
          rowErrors,
          rowKey,
          'row',
          `Band sequence ${sequence} is duplicated.`,
        );
      }

      seenSequences.add(sequence);

      if (sequence !== expectedSequence) {
        addRowFieldError(
          rowErrors,
          rowKey,
          'row',
          `Band sequence must be ${expectedSequence}.`,
        );
      }
    }

    const fromPct = parseDecimalValue(row.from_pct);
    const toPct = parseDecimalValue(row.to_pct);
    const payoutPct = parseDecimalValue(row.payout_pct);
    const interpolation = String(
      row.interpolation || '',
    ).toLowerCase();

    if (fromPct === null) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'from_pct',
        'Starting attainment is required.',
      );
    } else if (fromPct < 0) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'from_pct',
        'Starting attainment cannot be negative.',
      );
    }

    if (toPct === null) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'to_pct',
        'Ending attainment is required.',
      );
    }

    if (
      fromPct !== null
      && toPct !== null
      && toPct <= fromPct
    ) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'to_pct',
        'Ending attainment must be greater than starting attainment.',
      );
    }

    if (
      attainmentCeiling !== null
      && fromPct !== null
      && fromPct >= attainmentCeiling
    ) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'from_pct',
        'A band must begin below the recognized attainment ceiling.',
      );
    }

    if (
      attainmentCeiling !== null
      && toPct !== null
      && toPct > attainmentCeiling
    ) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'to_pct',
        'A band cannot extend beyond the recognized attainment ceiling.',
      );
    }

    if (payoutPct === null) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'payout_pct',
        'Payout percentage is required.',
      );
    } else {
      if (payoutPct < PAYOUT_MINIMUM) {
        addRowFieldError(
          rowErrors,
          rowKey,
          'payout_pct',
          'Payout percentage cannot be negative.',
        );
      }

      if (payoutPct > PAYOUT_MAXIMUM) {
        addRowFieldError(
          rowErrors,
          rowKey,
          'payout_pct',
          'Payout percentage cannot exceed 300%.',
        );
      }
    }

    if (!VALID_INTERPOLATION_VALUES.has(interpolation)) {
      addRowFieldError(
        rowErrors,
        rowKey,
        'interpolation',
        'Interpolation must be Linear or Step.',
      );
    }
  });

  if (orderedRows.length > 0) {
    const firstRow = orderedRows[0];
    const firstRowKey = firstRow.clientRowKey || 'row-0';
    const firstFromPct = parseDecimalValue(firstRow.from_pct);

    if (
      firstFromPct !== null
      && !numbersEqual(firstFromPct, 0)
    ) {
      addRowFieldError(
        rowErrors,
        firstRowKey,
        'from_pct',
        'The first band must start at 0%.',
      );
    }

    for (let index = 0; index < orderedRows.length - 1; index += 1) {
      const currentRow = orderedRows[index];
      const nextRow = orderedRows[index + 1];

      const currentRowKey = currentRow.clientRowKey || `row-${index}`;
      const nextRowKey = nextRow.clientRowKey || `row-${index + 1}`;

      const currentToPct = parseDecimalValue(currentRow.to_pct);
      const nextFromPct = parseDecimalValue(nextRow.from_pct);

      if (
        currentToPct !== null
        && nextFromPct !== null
        && !numbersEqual(currentToPct, nextFromPct)
      ) {
        if (nextFromPct > currentToPct) {
          addRowFieldError(
            rowErrors,
            currentRowKey,
            'to_pct',
            `This band ends at ${formatPercent(currentToPct)}, but the next band starts at ${formatPercent(nextFromPct)}.`,
          );

          addRowFieldError(
            rowErrors,
            nextRowKey,
            'from_pct',
            `There is a gap after Band ${index + 1}. Start this band at ${formatPercent(currentToPct)}.`,
          );
        } else {
          addRowFieldError(
            rowErrors,
            currentRowKey,
            'to_pct',
            `This band overlaps Band ${index + 2}.`,
          );

          addRowFieldError(
            rowErrors,
            nextRowKey,
            'from_pct',
            `This band overlaps the prior band by ${formatPercent(currentToPct - nextFromPct)}.`,
          );
        }
      }
    }

    const finalRow = orderedRows[orderedRows.length - 1];
    const finalRowKey = finalRow.clientRowKey || `row-${orderedRows.length - 1}`;
    const finalToPct = parseDecimalValue(finalRow.to_pct);

    if (
      attainmentCeiling !== null
      && finalToPct !== null
      && !numbersEqual(finalToPct, attainmentCeiling)
    ) {
      addRowFieldError(
        rowErrors,
        finalRowKey,
        'to_pct',
        `The final band must end at the ${formatPercent(attainmentCeiling)} recognized attainment ceiling.`,
      );
    }
  }

  if (
    attainmentCeiling !== null
    && defaultCeiling !== null
  ) {
    const mayExceedPayoutCeiling = orderedRows.some((row) => {
      const fromPct = parseDecimalValue(row.from_pct);
      const toPct = parseDecimalValue(row.to_pct);
      const payoutPct = parseDecimalValue(row.payout_pct);

      if (fromPct === null || toPct === null || payoutPct === null) {
        return false;
      }

      const maximumRecognizedAttainment = Math.min(toPct, attainmentCeiling);

      return normalizeComparableNumber(
        maximumRecognizedAttainment * payoutPct,
      ) > defaultCeiling;
    });

    if (mayExceedPayoutCeiling) {
      warnings.push(
        'The configured curve may produce earnings above the payout ceiling. Calculated payout will be limited by the plan\u2019s payout ceiling.',
      );
    }
  }

  const hasRowErrors = Object.values(rowErrors).some(
    (fields) =>
      Object.values(fields).some(
        (messages) => messages.length > 0,
      ),
  );

  return {
    isValid:
      tableErrors.length === 0
      && !hasRowErrors
      && orderedRows.length >= MINIMUM_BAND_COUNT,
    tableErrors,
    rowErrors,
    warnings,
  };
}

function normalizeDefaultBands(defaultRateTable) {
  if (
    !defaultRateTable
    || !Array.isArray(defaultRateTable.bands)
  ) {
    return [];
  }

  return resequenceRows(
    sortRowsBySequence(defaultRateTable.bands).map(
      (band, index) => ({
        clientRowKey: createClientRowKey('default'),
        rate_table_row_id: null,
        band_sequence: Number(band.band_sequence) || index + 1,
        from_pct: parseDecimalValue(band.from_pct),
        to_pct: parseDecimalValue(band.to_pct),
        payout_pct: parseDecimalValue(band.payout_pct),
        interpolation: String(band.interpolation || '').toLowerCase(),
        is_accelerator: Number(band.from_pct) >= 1.0,
      }),
    ),
  );
}

function buildRowPayload(rows) {
  return resequenceRows(sortRowsBySequence(rows)).map((row) => ({
    band_sequence: Number(row.band_sequence),
    from_pct: normalizeComparableNumber(row.from_pct),
    to_pct: normalizeComparableNumber(row.to_pct),
    payout_pct: normalizeComparableNumber(row.payout_pct),
    interpolation: String(row.interpolation).toLowerCase(),
  }));
}

export default function RateTableManager({
  mode = 'admin',
  period = null,
  embedded = true,
  planId,
  planComponentId,
  fiscalPeriodId = null,
  planTypeKey = null,
  roleId = null,
  segmentId = null,
  readOnly = false,
  onComplete = () => {},
}) {
  const { user } = useAuth();

  const [rateTable, setRateTable] = useState(null);
  const [savedRows, setSavedRows] = useState([]);
  const [draftRows, setDraftRows] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [loadError, setLoadError] = useState('');
  const [createError, setCreateError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [isDirty, setIsDirty] = useState(false);
  const [multipleActiveTables, setMultipleActiveTables] = useState(false);

  const loadRequestRef = useRef(0);
  const lastCompletionRef = useRef(null);

  const validation = useMemo(
    () => validateRateTableConfiguration(rateTable, draftRows),
    [rateTable, draftRows],
  );

  const persistedValidation = useMemo(
    () => validateRateTableConfiguration(rateTable, savedRows),
    [rateTable, savedRows],
  );

  const isBusy = isLoading || isCreating || isSaving;

  const completionState = useMemo(() => {
    if (
      isBusy
      || loadError
      || createError
      || saveError
      || multipleActiveTables
      || !rateTable
      || isDirty
    ) {
      return false;
    }

    return persistedValidation.isValid;
  }, [
    createError,
    isBusy,
    isDirty,
    loadError,
    multipleActiveTables,
    persistedValidation.isValid,
    rateTable,
    saveError,
  ]);

  useEffect(() => {
    if (lastCompletionRef.current === completionState) {
      return;
    }

    lastCompletionRef.current = completionState;
    onComplete(completionState);
  }, [completionState, onComplete]);

  const resetManagerState = useCallback(() => {
    setRateTable(null);
    setSavedRows([]);
    setDraftRows([]);
    setLoadError('');
    setCreateError('');
    setSaveError('');
    setSuccessMessage('');
    setIsDirty(false);
    setMultipleActiveTables(false);
  }, []);

  const applyLoadedRateTable = useCallback((table) => {
    const normalizedRows = normalizeLoadedRows(table?.rows);

    setRateTable(table);
    setSavedRows(cloneRows(normalizedRows));
    setDraftRows(cloneRows(normalizedRows));
    setIsDirty(false);
    setSaveError('');
    setCreateError('');
  }, []);

  const loadRateTable = useCallback(async () => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;

    resetManagerState();

    if (!planComponentId) {
      setLoadError(
        'Select a plan component before configuring its rate table.',
      );
      return;
    }

    setIsLoading(true);

    try {
      const payload = await apiGet(
        `/api/v1/rate-tables?plan_component_id=${encodeURIComponent(String(planComponentId))}`,
      );

      if (loadRequestRef.current !== requestId) {
        return;
      }

      const matchingTables = extractRateTableItems(payload).filter(
        (table) =>
          identifiersEqual(table.plan_component_id, planComponentId),
      );

      const activeTables = matchingTables.filter(
        (table) => table.is_active !== false,
      );

      if (activeTables.length > 1) {
        setMultipleActiveTables(true);
        setLoadError(
          'Multiple active rate tables are linked to this plan component. Resolve the data-integrity issue before editing.',
        );
        return;
      }

      const selectedTable =
        activeTables.length === 1
          ? activeTables[0]
          : matchingTables.length === 1
            ? matchingTables[0]
            : null;

      if (!selectedTable) {
        return;
      }

      if (!Array.isArray(selectedTable.rows)) {
        throw new Error(
          'The rate table response did not include its rate bands.',
        );
      }

      applyLoadedRateTable(selectedTable);
    } catch (error) {
      if (loadRequestRef.current !== requestId) {
        return;
      }

      setLoadError(
        error instanceof Error && error.message
          ? error.message
          : 'The rate table could not be loaded.',
      );
    } finally {
      if (loadRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [
    applyLoadedRateTable,
    planComponentId,
    resetManagerState,
  ]);

  // Fallback: load plan-type default_rate_table from GET /api/v1/plan-types
  const loadPlanTypeDefaultRateTable = useCallback(async () => {
    if (!planTypeKey) {
      return null;
    }

    const payload = await apiGet('/api/v1/plan-types');

    const matchingPlanType = extractPlanTypeItems(payload).find(
      (item) =>
        String(getPlanTypeKey(item) || '') === String(planTypeKey),
    );

    if (!matchingPlanType) {
      return null;
    }

    return extractDefaultRateTable(matchingPlanType);
  }, [planTypeKey]);

  useEffect(() => {
    loadRateTable();
  }, [loadRateTable]);

  const createRateTable = useCallback(async () => {
    if (readOnly || isCreating || isSaving || isLoading) {
      return;
    }

    if (!planId || !planComponentId) {
      setCreateError(
        'The plan and plan component are required to create a rate table.',
      );
      return;
    }

    setIsCreating(true);
    setCreateError('');
    setSaveError('');
    setSuccessMessage('');

    let createdTable = null;

    try {
      // Resolve role_id and segment_id from the component object passed via props.
      // The rate_tables router requires these as NOT NULL FK fields.
      // componentMeta is resolved from the surrounding PlanDetailView via props enrichment
      // or fetched fresh here from the component endpoint.
      let _roleId = roleId ?? null;
      let _segmentId = segmentId ?? null;

      // If not passed as props, fetch from plan-components to get role/segment
      if (!_roleId || !_segmentId) {
        try {
          const compData = await apiGet(
            `/api/v1/plan-components?plan_id=${planId}`,
          );
          const compItems = Array.isArray(compData)
            ? compData
            : compData?.data ?? compData?.plan_components ?? [];
          const matchingComp = compItems.find(
            (c) => String(c.plan_component_id) === String(planComponentId),
          );
          if (matchingComp) {
            _roleId = matchingComp.role_id;
            _segmentId = matchingComp.segment_id;
          }
        } catch (_) {
          // non-blocking — role/segment may still be null; router will reject with clear message
        }
      }

      const _rtCode = `RT-${planComponentId}`;
      const _rtName = `Rate Table — Plan Component ${planComponentId}`;

      // Pre-load catalog defaults so we can include ceiling values in the shell payload.
      // The router may require attainment_ceiling_pct / default_ceiling_pct.
      // We also need defaults for the rows POST that follows immediately.
      let _catalogDefaults = null;
      try {
        _catalogDefaults = await loadPlanTypeDefaultRateTable();
      } catch (_) {
        // non-blocking pre-load — will retry after shell creation
      }

      const createPayload = {
        plan_component_id:      planComponentId,
        role_id:                _roleId,
        segment_id:             _segmentId,
        rate_table_code:        _rtCode,
        rate_table_name:        _rtName,
        fiscal_period_id:
          fiscalPeriodId
          ?? period?.fiscal_period_id
          ?? null,
        attainment_ceiling_pct:
          _catalogDefaults?.attainment_ceiling_pct ?? null,
        default_ceiling_pct:
          _catalogDefaults?.default_ceiling_pct ?? null,
      };

      const createResponsePayload = await apiPost(
        '/api/v1/rate-tables',
        createPayload,
      );

      createdTable = extractCreatedRateTable(createResponsePayload);

      if (!createdTable?.rate_table_id) {
        throw new Error(
          'The create response did not include a rate table identifier.',
        );
      }

      // Resolve defaults — create response first, pre-loaded catalog second, fresh fetch last
      let defaultRateTable = extractDefaultRateTable(createResponsePayload);

      if (!defaultRateTable) {
        defaultRateTable = _catalogDefaults;
      }

      if (!defaultRateTable && planTypeKey) {
        defaultRateTable = await loadPlanTypeDefaultRateTable();
      }

      if (!defaultRateTable) {
        throw new Error(
          'Default rate bands are unavailable for this plan type. The rate table could not be initialized.',
        );
      }

      const defaultRows = normalizeDefaultBands(defaultRateTable);

      const tableForValidation = {
        ...createdTable,
        attainment_ceiling_pct:
          createdTable.attainment_ceiling_pct
          ?? defaultRateTable.attainment_ceiling_pct,
        default_ceiling_pct:
          createdTable.default_ceiling_pct
          ?? defaultRateTable.default_ceiling_pct,
      };

      const defaultValidation = validateRateTableConfiguration(
        tableForValidation,
        defaultRows,
      );

      if (!defaultValidation.isValid) {
        const validationMessage = [
          ...defaultValidation.tableErrors,
          ...Object.values(defaultValidation.rowErrors)
            .flatMap((fields) => Object.values(fields))
            .flat(),
        ][0];

        throw new Error(
          validationMessage || 'The default rate bands are invalid.',
        );
      }

      const rowsResponsePayload = await apiPost(
        `/api/v1/rate-tables/${createdTable.rate_table_id}/rows`,
        { rows: buildRowPayload(defaultRows) },
      );

      const responseRows = extractPersistedRows(rowsResponsePayload);

      if (responseRows) {
        applyLoadedRateTable({
          ...tableForValidation,
          rows: responseRows,
        });
      } else {
        await loadRateTable();
      }

      setSuccessMessage(
        'Rate table created from the plan type\u2019s default earnings curve.',
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'The rate table could not be created.';

      setCreateError(message);

      if (createdTable?.rate_table_id) {
        setRateTable({ ...createdTable, rows: [] });
        setSavedRows([]);
        setDraftRows([]);
      }
    } finally {
      setIsCreating(false);
    }
  }, [
    applyLoadedRateTable,
    fiscalPeriodId,
    isCreating,
    isLoading,
    isSaving,
    loadPlanTypeDefaultRateTable,
    loadRateTable,
    period?.fiscal_period_id,
    planComponentId,
    planId,
    planTypeKey,
    readOnly,
  ]);

  const updateDraftRow = useCallback(
    (clientRowKey, field, value) => {
      if (readOnly || isBusy) {
        return;
      }

      setSuccessMessage('');
      setSaveError('');

      setDraftRows((currentRows) =>
        currentRows.map((row) => {
          if (row.clientRowKey !== clientRowKey) {
            return row;
          }

          if (
            field === 'from_pct'
            || field === 'to_pct'
            || field === 'payout_pct'
          ) {
            const decimalValue = percentInputToDecimal(value);

            return {
              ...row,
              [field]: decimalValue,
              is_accelerator:
                field === 'from_pct'
                  ? Number(decimalValue) >= 1.0
                  : row.is_accelerator,
            };
          }

          if (field === 'interpolation') {
            return {
              ...row,
              interpolation: String(value).toLowerCase(),
            };
          }

          return row;
        }),
      );

      setIsDirty(true);
    },
    [isBusy, readOnly],
  );

  const addBand = useCallback(() => {
    if (
      readOnly
      || isBusy
      || !rateTable
      || draftRows.length === 0
    ) {
      return;
    }

    const orderedRows = resequenceRows(sortRowsBySequence(draftRows));
    const finalRow = orderedRows[orderedRows.length - 1];
    const attainmentCeiling = parseDecimalValue(rateTable.attainment_ceiling_pct);

    const newRow = {
      clientRowKey: createClientRowKey('new'),
      rate_table_row_id: null,
      band_sequence: orderedRows.length + 1,
      from_pct: parseDecimalValue(finalRow.to_pct),
      to_pct: attainmentCeiling,
      payout_pct: parseDecimalValue(finalRow.payout_pct),
      interpolation:
        VALID_INTERPOLATION_VALUES.has(
          String(finalRow.interpolation).toLowerCase(),
        )
          ? String(finalRow.interpolation).toLowerCase()
          : 'linear',
      is_accelerator: Number(finalRow.to_pct) >= 1.0,
    };

    setDraftRows([...orderedRows, newRow]);
    setIsDirty(true);
    setSaveError('');
    setSuccessMessage('');
  }, [
    draftRows,
    isBusy,
    rateTable,
    readOnly,
  ]);

  const removeBand = useCallback(
    (clientRowKey) => {
      if (
        readOnly
        || isBusy
        || draftRows.length <= MINIMUM_BAND_COUNT
      ) {
        return;
      }

      setDraftRows((currentRows) =>
        resequenceRows(
          currentRows.filter((row) => row.clientRowKey !== clientRowKey),
        ),
      );

      setIsDirty(true);
      setSaveError('');
      setSuccessMessage('');
    },
    [
      draftRows.length,
      isBusy,
      readOnly,
    ],
  );

  const moveBand = useCallback(
    (clientRowKey, direction) => {
      if (readOnly || isBusy) {
        return;
      }

      setDraftRows((currentRows) => {
        const orderedRows = resequenceRows(sortRowsBySequence(currentRows));
        const currentIndex = orderedRows.findIndex(
          (row) => row.clientRowKey === clientRowKey,
        );

        if (currentIndex < 0) {
          return currentRows;
        }

        const targetIndex =
          direction === 'up'
            ? currentIndex - 1
            : currentIndex + 1;

        if (targetIndex < 0 || targetIndex >= orderedRows.length) {
          return currentRows;
        }

        const reorderedRows = [...orderedRows];
        const [movedRow] = reorderedRows.splice(currentIndex, 1);
        reorderedRows.splice(targetIndex, 0, movedRow);

        return resequenceRows(reorderedRows);
      });

      setIsDirty(true);
      setSaveError('');
      setSuccessMessage('');
    },
    [isBusy, readOnly],
  );

  const cancelChanges = useCallback(() => {
    if (readOnly || isSaving) {
      return;
    }

    setDraftRows(cloneRows(savedRows));
    setIsDirty(false);
    setSaveError('');
    setSuccessMessage('');
  }, [
    isSaving,
    readOnly,
    savedRows,
  ]);

  // Recovery handler: load default bands into an existing shell that has no rows
  const loadDefaultBands = useCallback(async () => {
    if (
      readOnly
      || isBusy
      || !rateTable?.rate_table_id
    ) {
      return;
    }

    if (!planTypeKey) {
      setCreateError(
        'Default rate bands are unavailable because the plan type could not be determined.',
      );
      return;
    }

    setIsCreating(true);
    setCreateError('');
    setSaveError('');
    setSuccessMessage('');

    try {
      const defaultRateTable = await loadPlanTypeDefaultRateTable();

      if (!defaultRateTable) {
        throw new Error(
          'Default rate bands are unavailable for this plan type. The rate table could not be initialized.',
        );
      }

      const defaultRows = normalizeDefaultBands(defaultRateTable);

      const tableForValidation = {
        ...rateTable,
        attainment_ceiling_pct:
          rateTable.attainment_ceiling_pct
          ?? defaultRateTable.attainment_ceiling_pct,
        default_ceiling_pct:
          rateTable.default_ceiling_pct
          ?? defaultRateTable.default_ceiling_pct,
      };

      const defaultValidation = validateRateTableConfiguration(
        tableForValidation,
        defaultRows,
      );

      if (!defaultValidation.isValid) {
        const firstValidationError = [
          ...defaultValidation.tableErrors,
          ...Object.values(defaultValidation.rowErrors)
            .flatMap((fields) => Object.values(fields))
            .flat(),
        ].find(Boolean);

        throw new Error(
          firstValidationError || 'The default rate bands are invalid.',
        );
      }

      const payload = await apiPost(
        `/api/v1/rate-tables/${rateTable.rate_table_id}/rows`,
        { rows: buildRowPayload(defaultRows) },
      );

      const persistedRows = extractPersistedRows(payload);

      if (persistedRows) {
        applyLoadedRateTable({
          ...tableForValidation,
          rows: persistedRows,
        });
      } else {
        await loadRateTable();
      }

      setSuccessMessage('Default rate bands loaded successfully.');
    } catch (error) {
      setCreateError(
        error instanceof Error && error.message
          ? error.message
          : 'The default rate bands could not be loaded.',
      );
    } finally {
      setIsCreating(false);
    }
  }, [
    applyLoadedRateTable,
    isBusy,
    loadPlanTypeDefaultRateTable,
    loadRateTable,
    planTypeKey,
    rateTable,
    readOnly,
  ]);

  const saveChanges = useCallback(async () => {
    if (
      readOnly
      || isBusy
      || !rateTable?.rate_table_id
      || !isDirty
    ) {
      return;
    }

    const currentValidation = validateRateTableConfiguration(
      rateTable,
      draftRows,
    );

    if (!currentValidation.isValid) {
      setSaveError(
        'Correct the highlighted rate-table errors before saving.',
      );
      return;
    }

    setIsSaving(true);
    setSaveError('');
    setCreateError('');
    setSuccessMessage('');

    try {
      const payload = await apiPost(
        `/api/v1/rate-tables/${rateTable.rate_table_id}/rows`,
        { rows: buildRowPayload(draftRows) },
      );

      const persistedRows = extractPersistedRows(payload);

      if (persistedRows) {
        const normalizedRows = normalizeLoadedRows(persistedRows);
        setSavedRows(cloneRows(normalizedRows));
        setDraftRows(cloneRows(normalizedRows));
        setIsDirty(false);
      } else {
        await loadRateTable();
      }

      setSuccessMessage('Rate table changes saved successfully.');
    } catch (error) {
      setSaveError(
        error instanceof Error && error.message
          ? error.message
          : 'The rate table could not be saved. Review the values and try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    draftRows,
    isBusy,
    isDirty,
    loadRateTable,
    rateTable,
    readOnly,
  ]);

  const retryLoad = useCallback(() => {
    if (isBusy) {
      return;
    }

    loadRateTable();
  }, [
    isBusy,
    loadRateTable,
  ]);

  const orderedDraftRows = useMemo(
    () => resequenceRows(sortRowsBySequence(draftRows)),
    [draftRows],
  );

  const hasConfiguredRows = orderedDraftRows.length > 0;

  const completionLabel = useMemo(() => {
    if (readOnly) return 'Read only';
    if (!rateTable) return 'Not created';
    if (completionState) return 'Complete';
    return 'Incomplete';
  }, [completionState, rateTable, readOnly]);

  const completionBadgeClass = useMemo(() => {
    if (readOnly) return 'modifier-manager-badge modifier-manager-badge--readonly';
    if (!rateTable) return 'modifier-manager-badge modifier-manager-badge--not-created';
    if (completionState) return 'modifier-manager-badge modifier-manager-badge--complete';
    return 'modifier-manager-badge modifier-manager-badge--incomplete';
  }, [completionState, rateTable, readOnly]);

  const contextPeriodName =
    period?.period_name
    || (fiscalPeriodId ? `Fiscal period ${fiscalPeriodId}` : null);

  const rateTableName = rateTable?.rate_table_name || 'Rate Table';
  const effectiveDateLabel = rateTable?.effective_date || 'Not configured';
  const endDateLabel = rateTable?.end_date || 'No end date';
  const attainmentCeilingLabel = formatPercent(rateTable?.attainment_ceiling_pct);
  const defaultCeilingLabel = formatPercent(rateTable?.default_ceiling_pct);

  const canCreate = !readOnly && !isBusy && !rateTable && Boolean(planComponentId);
  const canLoadDefaults =
    !readOnly && !isBusy && Boolean(rateTable?.rate_table_id) && !hasConfiguredRows && Boolean(planTypeKey);
  const canAddBand = !readOnly && !isBusy && Boolean(rateTable) && hasConfiguredRows;
  const canSave = !readOnly && !isBusy && Boolean(rateTable?.rate_table_id) && isDirty && validation.isValid;
  const canCancel = !readOnly && !isSaving && isDirty;

  const getFieldErrors = useCallback(
    (rowKey, field) => validation.rowErrors?.[rowKey]?.[field] || [],
    [validation.rowErrors],
  );

  const getRowErrors = useCallback(
    (rowKey) => validation.rowErrors?.[rowKey]?.row || [],
    [validation.rowErrors],
  );

  const renderFieldErrors = useCallback((messages) => {
    if (!messages || messages.length === 0) {
      return null;
    }

    return (
      <div className="modifier-manager-field-errors">
        {messages.map((message, index) => (
          <div
            className="modifier-manager-field-error"
            key={`${message}-${index}`}
            role="alert"
          >
            {message}
          </div>
        ))}
      </div>
    );
  }, []);

  const renderValidationSummary = () => {
    const rowErrorMessages = Object.values(validation.rowErrors)
      .flatMap((fields) => Object.values(fields).flat())
      .filter(Boolean);

    const allErrors = [
      ...validation.tableErrors,
      ...rowErrorMessages,
    ];

    if (allErrors.length === 0) {
      return null;
    }

    const uniqueErrors = [...new Set(allErrors)];

    return (
      <div className="modifier-manager-validation-summary" role="alert">
        <h4>Correct before saving</h4>
        <ul>
          {uniqueErrors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div
      className={[
        'modifier-manager-card',
        embedded ? 'modifier-manager-card--embedded' : '',
        mode === 'wizard' ? 'modifier-manager-card--wizard' : 'modifier-manager-card--admin',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="modifier-manager-header">
        <div className="modifier-manager-header-copy">
          <h3>Rate Table</h3>
          <p>Define how attainment converts to payout for this plan component.</p>
        </div>

        <span className={completionBadgeClass}>
          {completionLabel}
        </span>
      </div>

      <div className="modifier-manager-body">
        {isLoading && (
          <div className="modifier-manager-loading" aria-live="polite">
            Loading rate table\u2026
          </div>
        )}

        {!isLoading && loadError && (
          <div className="modifier-manager-error-state" role="alert">
            <h4>Rate table unavailable</h4>
            <p>{loadError}</p>

            <button
              type="button"
              className="secondary-button"
              onClick={retryLoad}
              disabled={isBusy}
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !loadError && !rateTable && (
          <div className="modifier-manager-empty-state">
            <h4>No rate table configured</h4>

            <p>
              {readOnly
                ? 'No rate table is configured for this plan component.'
                : 'No rate table has been created for this plan component. Create one using the plan type\u2019s default earnings curve.'}
            </p>

            {!readOnly && (
              <button
                type="button"
                className="primary-button"
                onClick={createRateTable}
                disabled={!canCreate}
              >
                {isCreating ? 'Creating rate table\u2026' : 'Create Rate Table'}
              </button>
            )}

            {createError && (
              <div className="modifier-manager-inline-error" role="alert">
                {createError}
              </div>
            )}
          </div>
        )}

        {!isLoading && !loadError && rateTable && (
          <>
            <div className="modifier-manager-context">
              <div className="modifier-manager-context-primary">
                <strong>{rateTableName}</strong>

                {rateTable.rate_table_code && (
                  <span className="modifier-manager-code">
                    {rateTable.rate_table_code}
                  </span>
                )}
              </div>

              <dl className="modifier-manager-context-grid">
                <div>
                  <dt>Plan component</dt>
                  <dd>Selected component</dd>
                </div>

                {contextPeriodName && (
                  <div>
                    <dt>Period</dt>
                    <dd>{contextPeriodName}</dd>
                  </div>
                )}

                <div>
                  <dt>Effective date</dt>
                  <dd>{effectiveDateLabel}</dd>
                </div>

                <div>
                  <dt>End date</dt>
                  <dd>{endDateLabel}</dd>
                </div>
              </dl>
            </div>

            {/* S28.13.4 — Ceiling summary — static text, not editable inputs */}
            <div className="modifier-manager-ceiling-summary">
              <div className="modifier-manager-static-field">
                <span className="modifier-manager-static-label">
                  Recognized attainment ceiling
                </span>
                <strong>{attainmentCeilingLabel}</strong>
              </div>

              <div className="modifier-manager-static-field">
                <span className="modifier-manager-static-label">
                  Payout ceiling
                </span>
                <strong>{defaultCeilingLabel} of variable OTE</strong>
              </div>
            </div>

            {rateTable.is_active === false && (
              <div className="modifier-manager-warning" role="status">
                This rate table is inactive and cannot satisfy the wizard completion requirement.
              </div>
            )}

            {rateTable.approved_by && (
              <div className="modifier-manager-info" role="status">
                This rate table was approved with its linked plan component.
              </div>
            )}

            {validation.warnings.map((warning) => (
              <div className="modifier-manager-warning" key={warning} role="status">
                {warning}
              </div>
            ))}

            {createError && (
              <div className="modifier-manager-inline-error" role="alert">
                {createError}
              </div>
            )}

            {saveError && (
              <div className="modifier-manager-inline-error" role="alert">
                {saveError}
              </div>
            )}

            {successMessage && (
              <div className="modifier-manager-success" role="status" aria-live="polite">
                {successMessage}
              </div>
            )}

            {!hasConfiguredRows && (
              <div className="modifier-manager-empty-bands">
                <h4>No rate bands configured</h4>

                <p>The rate-table shell exists, but no band data is available.</p>

                {!readOnly && (
                  <button
                    type="button"
                    className="primary-button"
                    onClick={loadDefaultBands}
                    disabled={!canLoadDefaults}
                  >
                    {isCreating ? 'Loading default bands\u2026' : 'Load Default Bands'}
                  </button>
                )}

                {!readOnly && !planTypeKey && (
                  <div className="modifier-manager-inline-error" role="alert">
                    Default bands cannot be loaded because the plan type key is unavailable.
                  </div>
                )}
              </div>
            )}

            {hasConfiguredRows && (
              <>
                <div className="modifier-manager-table-wrapper">
                  <table className="modifier-manager-table">
                    <thead>
                      <tr>
                        <th scope="col">Band</th>
                        <th scope="col">From attainment</th>
                        <th scope="col">To attainment</th>
                        <th scope="col">Payout</th>
                        <th scope="col">Interpolation</th>
                        <th scope="col">Accelerator</th>
                        {!readOnly && <th scope="col">Actions</th>}
                      </tr>
                    </thead>

                    <tbody>
                      {orderedDraftRows.map((row, index) => {
                        const fromErrors = getFieldErrors(row.clientRowKey, 'from_pct');
                        const toErrors = getFieldErrors(row.clientRowKey, 'to_pct');
                        const payoutErrors = getFieldErrors(row.clientRowKey, 'payout_pct');
                        const interpolationErrors = getFieldErrors(row.clientRowKey, 'interpolation');
                        const rowLevelErrors = getRowErrors(row.clientRowKey);

                        const rowHasErrors =
                          fromErrors.length > 0
                          || toErrors.length > 0
                          || payoutErrors.length > 0
                          || interpolationErrors.length > 0
                          || rowLevelErrors.length > 0;

                        return (
                          <tr
                            key={row.clientRowKey}
                            className={rowHasErrors ? 'modifier-manager-row--error' : ''}
                          >
                            <td className="modifier-manager-band-number">
                              <strong>{row.band_sequence}</strong>
                              {renderFieldErrors(rowLevelErrors)}
                            </td>

                            <td>
                              {readOnly ? (
                                <span>{formatPercent(row.from_pct)}</span>
                              ) : (
                                <>
                                  <div className="modifier-manager-percent-input">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={decimalToPercentInput(row.from_pct)}
                                      onChange={(e) =>
                                        updateDraftRow(row.clientRowKey, 'from_pct', e.target.value)
                                      }
                                      aria-label={`Band ${row.band_sequence} starting attainment`}
                                      aria-invalid={fromErrors.length > 0}
                                      disabled={isBusy}
                                    />
                                    <span>%</span>
                                  </div>
                                  {renderFieldErrors(fromErrors)}
                                </>
                              )}
                            </td>

                            <td>
                              {readOnly ? (
                                <span>{formatPercent(row.to_pct)}</span>
                              ) : (
                                <>
                                  <div className="modifier-manager-percent-input">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={decimalToPercentInput(row.to_pct)}
                                      onChange={(e) =>
                                        updateDraftRow(row.clientRowKey, 'to_pct', e.target.value)
                                      }
                                      aria-label={`Band ${row.band_sequence} ending attainment`}
                                      aria-invalid={toErrors.length > 0}
                                      disabled={isBusy}
                                    />
                                    <span>%</span>
                                  </div>
                                  {renderFieldErrors(toErrors)}
                                </>
                              )}
                            </td>

                            <td>
                              {readOnly ? (
                                <span>{formatPercent(row.payout_pct)}</span>
                              ) : (
                                <>
                                  <div className="modifier-manager-percent-input">
                                    <input
                                      type="number"
                                      min="0"
                                      max="300"
                                      step="0.01"
                                      value={decimalToPercentInput(row.payout_pct)}
                                      onChange={(e) =>
                                        updateDraftRow(row.clientRowKey, 'payout_pct', e.target.value)
                                      }
                                      aria-label={`Band ${row.band_sequence} payout percentage`}
                                      aria-invalid={payoutErrors.length > 0}
                                      disabled={isBusy}
                                    />
                                    <span>%</span>
                                  </div>
                                  {renderFieldErrors(payoutErrors)}
                                </>
                              )}
                            </td>

                            <td>
                              {readOnly ? (
                                <span>
                                  {row.interpolation === 'step' ? 'Step' : 'Linear'}
                                </span>
                              ) : (
                                <>
                                  <select
                                    value={row.interpolation}
                                    onChange={(e) =>
                                      updateDraftRow(row.clientRowKey, 'interpolation', e.target.value)
                                    }
                                    aria-label={`Band ${row.band_sequence} interpolation`}
                                    aria-invalid={interpolationErrors.length > 0}
                                    disabled={isBusy}
                                  >
                                    <option value="linear">Linear</option>
                                    <option value="step">Step</option>
                                  </select>
                                  {renderFieldErrors(interpolationErrors)}
                                </>
                              )}
                            </td>

                            <td>
                              <span
                                className={[
                                  'modifier-manager-accelerator-badge',
                                  Number(row.from_pct) >= 1
                                    ? 'modifier-manager-accelerator-badge--active'
                                    : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                {Number(row.from_pct) >= 1 ? 'Yes' : 'No'}
                              </span>
                            </td>

                            {!readOnly && (
                              <td>
                                <div className="modifier-manager-row-actions">
                                  <button
                                    type="button"
                                    className="icon-button"
                                    onClick={() => moveBand(row.clientRowKey, 'up')}
                                    disabled={isBusy || index === 0}
                                    aria-label={`Move Band ${row.band_sequence} up`}
                                    title="Move up"
                                  >
                                    ↑
                                  </button>

                                  <button
                                    type="button"
                                    className="icon-button"
                                    onClick={() => moveBand(row.clientRowKey, 'down')}
                                    disabled={isBusy || index === orderedDraftRows.length - 1}
                                    aria-label={`Move Band ${row.band_sequence} down`}
                                    title="Move down"
                                  >
                                    ↓
                                  </button>

                                  <button
                                    type="button"
                                    className="text-button text-button--danger"
                                    onClick={() => removeBand(row.clientRowKey)}
                                    disabled={
                                      isBusy
                                      || orderedDraftRows.length <= MINIMUM_BAND_COUNT
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!readOnly && (
                  <div className="modifier-manager-table-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={addBand}
                      disabled={!canAddBand}
                    >
                      Add Band
                    </button>

                    <span className="modifier-manager-band-count">
                      {orderedDraftRows.length}{' '}
                      {orderedDraftRows.length === 1 ? 'band' : 'bands'}
                    </span>
                  </div>
                )}

                {!readOnly && renderValidationSummary()}

                {readOnly && !persistedValidation.isValid && (
                  <div className="modifier-manager-validation-summary" role="alert">
                    <h4>Rate table is incomplete</h4>

                    <ul>
                      {[
                        ...persistedValidation.tableErrors,
                        ...Object.values(persistedValidation.rowErrors)
                          .flatMap((fields) => Object.values(fields).flat())
                          .filter(Boolean),
                      ]
                        .filter((message, index, messages) => messages.indexOf(message) === index)
                        .map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                    </ul>
                  </div>
                )}

                {!readOnly && (
                  <div className="modifier-manager-footer">
                    <div className="modifier-manager-footer-status">
                      {isDirty && <span>Unsaved changes</span>}

                      {!isDirty && persistedValidation.isValid && (
                        <span>All bands are saved and valid.</span>
                      )}
                    </div>

                    <div className="modifier-manager-footer-actions">
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={cancelChanges}
                        disabled={!canCancel}
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        className="primary-button"
                        onClick={saveChanges}
                        disabled={!canSave}
                      >
                        {isSaving ? 'Saving\u2026' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {readOnly && (
              <div className="modifier-manager-readonly-note">
                Rate-table changes are unavailable in read-only mode.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
